/* Trips · school trips, upcoming, today and past. Design: ConsoleTrips.dc.html.
   Since 2026-10-28 the trip planner (SchoolTripComposer, restyled) opens
   here, and a trip opens its own panel (?trip=<id>): details, its temporary
   logins, cancelling it. Both used to open the classic dashboard. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import SchoolTripComposer, { ComposerBus } from '../../components/SchoolTripComposer';
import { TripSheet } from '../TripSheet';
import { useFleet } from '../data/FleetData';
import type { Trip } from '../data/model';
import { usePage, useShell } from '../Shell';
import { ChipTone, PageBody, PageSkeleton, StaleBanner, dayMonth, hhmm, plural } from '../kit';

type Tab = 'up' | 'today' | 'past';

const TONE: Record<Trip['status'], ChipTone> = {
  confirmed: 'teal', live: 'teal', needs_driver: 'warn', draft: 'mute', done: 'ok', cancelled: 'mute',
};

const PlusIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const tripName = (t: Trip) => t.title + (t.destination ? ` · ${t.destination}` : '');

/** "07:30 → 15:00 · 2 buses · 64 children", leaving out what the trip does not record. */
function tripLine(t: Trip, withDate: boolean): string {
  const parts: string[] = [];
  if (t.start) {
    let time = withDate ? dayMonth(t.start) : hhmm(t.start);
    if (!withDate && t.end) time += ` → ${t.end.toDateString() === t.start.toDateString() ? hhmm(t.end) : `${dayMonth(t.end)} ${hhmm(t.end)}`}`;
    parts.push(time);
  } else {
    parts.push('No date yet');
  }
  parts.push(t.busIds.length ? plural(t.busIds.length, 'bus', 'buses') : 'No bus yet');
  if (t.children != null) parts.push(plural(t.children, 'child', 'children'));
  return parts.join(' · ');
}

/** The planner's bus list: the driver names (from the bus's driverIds and
 *  every driver whose own busId is this bus, as classic did) and the roster. */
function composerBuses(f: ReturnType<typeof useFleet>): ComposerBus[] {
  return f.buses.map((b) => {
    const ids = new Set((Array.isArray(b.raw.driverIds) ? b.raw.driverIds : []).map(String));
    f.drivers.forEach((d) => { if (d.busId === b.id) ids.add(d.id); });
    const names = [...ids].map((id) => f.drivers.find((d) => d.id === id)?.name).filter(Boolean);
    const roster = (Array.isArray(b.raw.children) ? b.raw.children : []) as Record<string, unknown>[];
    return {
      id: b.id, name: b.name, plate: b.plate,
      driverLabel: names.length ? names.join(', ') : undefined,
      children: roster.filter((c) => c && c.temporaryTripChild !== true).map((c) => ({
        id: String(c.childId || c.childName || ''), name: String(c.childName || c.name || ''),
        parentId: c.parentId ? String(c.parentId) : undefined, classLabel: c.className ? String(c.className) : undefined,
      })),
    };
  });
}

const Trips: React.FC = () => {
  const f = useFleet();
  const { dark } = useShell();
  const [tab, setTab] = useState<Tab>('up');
  const [planning, setPlanning] = useState(false);
  const [params, setParams] = useSearchParams();
  const tripId = params.get('trip');
  const trip = tripId ? f.trips.find((x) => x.id === tripId) || null : null;
  const openTrip = (id: string) => { const n = new URLSearchParams(params); n.set('trip', id); setParams(n, { replace: !!tripId }); };
  const closeTrip = useCallback(() => { const n = new URLSearchParams(params); n.delete('trip'); setParams(n, { replace: true }); }, [params, setParams]);
  const [sheetTop, setSheetTop] = useState(64);
  useEffect(() => {
    if (!trip) return undefined;
    const h = (document.querySelector('.tc .main > .top') as HTMLElement | null)?.offsetHeight;
    if (h) setSheetTop(h);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[role=dialog]')) { e.preventDefault(); closeTrip(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [trip, closeTrip]);

  const { up, today, past } = useMemo(() => {
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dayEnd = dayStart + 86400000;
    const u: Trip[] = []; const t: Trip[] = []; const p: Trip[] = [];
    f.trips.forEach((trip) => {
      const s = trip.start?.getTime() ?? null;
      if (trip.status === 'done' || trip.status === 'cancelled') p.push(trip);
      else if (trip.status === 'live' || (s != null && s >= dayStart && s < dayEnd)) t.push(trip);
      else if (s == null || s >= dayEnd) u.push(trip);
      else p.push(trip);
    });
    // Upcoming soonest first (undated drafts last); past newest first.
    u.sort((a, b) => (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity));
    p.sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0));
    return { up: u, today: t, past: p };
  }, [f.trips]);

  const next = up.find((t) => t.start);
  const noBus = up.find((t) => t.status === 'needs_driver');
  usePage({
    key: 'ConsoleTrips', title: 'Trips', zanaContext: `Trips · ${tab === 'up' ? 'upcoming' : tab === 'today' ? 'today' : 'past'}`,
    zanaIntro: 'I can find a free driver and draft the letter to parents.',
    prompts: [
      ...(noBus ? [`Who can drive ${noBus.title}${noBus.start ? ` on ${dayMonth(noBus.start)}` : ''}?`] : []),
      next ? `Draft the parent letter for ${next.title}` : 'Draft a parent letter for a school trip',
      ...(past[0] ? [`How did ${past[0].title} go?`] : []),
    ],
  });

  const list = tab === 'up' ? up : tab === 'today' ? today : past;

  const empty = (() => {
    if (tab === 'today') {
      return {
        title: 'No trips today',
        body: `${next && next.start ? `The next one is ${tripName(next)} on ${next.start.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}. ` : 'No trips are planned yet. '}Plan a trip and assign its buses in the trip planner.`,
      };
    }
    if (tab === 'up') return { title: 'No trips planned', body: 'Plan a trip, choose its buses and drivers, and it shows here until the day.' };
    return { title: 'No past trips yet', body: 'Trips appear here once they are done or cancelled.' };
  })();

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: '1040px' }}>
        {f.error && <StaleBanner what="trips" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">School trips</h1>
            <p className="sub">Plan a trip, then assign its buses and drivers.</p>
          </div>
          <button type="button" className="btn b-ink" onClick={() => setPlanning(true)}><PlusIcon />Plan a trip</button>
        </div>
        <div className="seg" role="tablist" aria-label="Which trips">
          <button type="button" role="tab" aria-selected={tab === 'up'} className={tab === 'up' ? 'on' : ''} onClick={() => setTab('up')}>Upcoming {up.length}</button>
          <button type="button" role="tab" aria-selected={tab === 'today'} className={tab === 'today' ? 'on' : ''} onClick={() => setTab('today')}>Today {today.length}</button>
          <button type="button" role="tab" aria-selected={tab === 'past'} className={tab === 'past' ? 'on' : ''} onClick={() => setTab('past')}>Past {past.length}</button>
        </div>
        <section className="panel" style={{ overflow: 'hidden' }}>
          {list.length === 0 && (
            <div className="empty">
              <span className="eic">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M16 3v4M8 3v4M3 10h18" />
                </svg>
              </span>
              <b style={{ fontSize: '17px' }}>{empty.title}</b>
              <p className="sub" style={{ maxWidth: '380px' }}>{empty.body}</p>
              {tab !== 'past' && (
                <button type="button" className="btn b-ink" onClick={() => setPlanning(true)} style={{ marginTop: '6px' }}><PlusIcon />Plan a trip</button>
              )}
            </div>
          )}
          {tab !== 'past' && list.map((t) => (
            <div key={t.id} className="todo" style={{ padding: '14px 20px' }}>
              <div style={{ width: '56px', flex: 'none', textAlign: 'center', border: '1px solid var(--line)', borderRadius: '10px', padding: '6px 0' }}>
                {t.start ? (<>
                  <div className="mono" style={{ fontSize: '11px' }}>{t.start.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                  <div className="big" style={{ fontSize: '24px', lineHeight: '26px' }}>{t.start.getDate()}</div>
                  <div className="mono" style={{ fontSize: '11px' }}>{t.start.toLocaleDateString('en-GB', { month: 'short' })}</div>
                </>) : (
                  <div className="big" style={{ fontSize: '24px', lineHeight: '26px', color: 'var(--faint)' }}>—</div>
                )}
              </div>
              <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <b style={{ fontSize: '16px' }}>{tripName(t)}</b>
                <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>{tripLine(t, false)}</span>
              </div>
              <span className={`chip c-${TONE[t.status]}`}>{t.statusLabel}</span>
              <button type="button" className="ibtn" onClick={() => openTrip(t.id)} aria-label={`Open ${t.title}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </div>
          ))}
          {tab === 'past' && list.map((t) => (
            <div key={t.id} className="todo" style={{ padding: '12px 20px' }}>
              <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                <b>{tripName(t)}</b>
                <span className="sm" style={{ color: 'var(--muted)' }}>{tripLine(t, true)}</span>
              </div>
              <span className={`chip c-${TONE[t.status]}`}>{t.statusLabel}</span>
              <button type="button" className="ibtn" onClick={() => openTrip(t.id)} aria-label={`Open ${t.title}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </div>
          ))}
        </section>
      </div>
      {trip && <TripSheet f={f} trip={trip} top={sheetTop} onClose={closeTrip} />}
      {/* Portalled to <body>: inside the console the page wrapper traps its
          fixed position and the console's form styles leak into its fields. */}
      {planning && f.org.code && createPortal(
        <SchoolTripComposer fleetOrgCode={f.org.code} canHire buses={composerBuses(f)} dark={dark} onClose={() => setPlanning(false)} />,
        document.body,
      )}
    </PageBody>
  );
};

export default Trips;
